import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { MfaEnrollmentDialog } from "./MfaEnrollmentDialog";
import { BreakGlassPasswordSection } from "./BreakGlassPasswordSection";
import { isBreakGlassOwner, isBreakGlassReady, needsBreakGlassPassword } from "./breakGlassPolicy";

import LoadingSpinner from "@/components/LoadingSpinner";
import { Badge, BadgeSize, BadgeVariant } from "@/components/ui/Badge";
import { Button } from "@/components/ui/button";
import HelpTooltip from "@/components/ui/HelpTooltip";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/switch";
import { useAuth } from "@/context/auth-context";
import { useUpdateUser } from "@/queries/user";
import { useDeleteMfaFactor } from "@/queries/auth/mfa";
import { useMfaPolicy } from "@/queries/identity/useMfaPolicy";
import { useMfaVerification } from "@/context/mfa-verification-context";

const formSchema = z.object({
  name: z
    .string()
    .regex(
      /^[\p{L}\p{M}\s']+$/u,
      "Name can only contain letters, spaces, hyphens, apostrophes, and periods",
    )
    .min(1, "Name is required")
    .max(255, "Name must be less than 255 characters"),
  email: z.string().email(),
});

const AccountSettings: React.FC = () => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
    },
  });
  const auth = useAuth();
  const { mutate: updateUser } = useUpdateUser();
  const { verifyMfa } = useMfaVerification();
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [enrollmentModalOpen, setEnrollmentModalOpen] = useState(false);
  const { mutate: deleteMfa, isLoading: isDeleting } = useDeleteMfaFactor();
  const { data: mfaPolicy, isLoading: isMfaPolicyLoading } = useMfaPolicy();
  const canSelfManageMfa = mfaPolicy?.can_self_manage ?? true;
  const isOwnerBreakGlass = isBreakGlassOwner(mfaPolicy);
  const needsBreakGlassPasswordSetup = needsBreakGlassPassword(mfaPolicy);
  const isBreakGlassReadyState = isBreakGlassReady(mfaPolicy) && mfaEnabled;

  const securityDescription = (() => {
    if (!canSelfManageMfa) {
      return "Authentication is managed by your organization through SSO.";
    }
    if (isOwnerBreakGlass) {
      return "Set up emergency access before enabling Require SSO for your organization.";
    }
    return "Manage multi-factor authentication for your account.";
  })();

  const mfaHelpTooltip = (() => {
    if (!canSelfManageMfa) {
      return "MFA is managed by your organization's identity provider. Configure org-wide MFA in your IdP (for example Azure Entra Conditional Access or Okta). PromptLayer does not prompt SSO users for a second factor.";
    }
    if (isOwnerBreakGlass) {
      return "Enroll MFA here for emergency email/password login when Require SSO is enabled. This MFA does not apply when you sign in through SSO — your IdP handles MFA for SSO sign-in.";
    }
    return "Add a second factor to protect email/password sign-in to your PromptLayer account.";
  })();

  useEffect(() => {
    if (auth?.user) {
      form.reset(auth.user);
      const userWithMfa = auth.user as {
        workos_mfa_factor_id?: string | null;
        mfa_type?: string | null;
      };
      const isMfaEnrolled = !!(userWithMfa?.workos_mfa_factor_id || userWithMfa?.mfa_type);
      setMfaEnabled(isMfaEnrolled);
    }
  }, [auth?.user, form]);

  const handleMfaToggle = async (enabled: boolean) => {
    if (enabled) {
      setEnrollmentModalOpen(true);
    } else {
      const verified = await verifyMfa();
      if (verified) {
        deleteMfa(undefined, {
          onSuccess: () => {
            setMfaEnabled(false);
          },
        });
      } else {
        setMfaEnabled(true);
      }
    }
  };

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    updateUser({
      name: data.name,
    });
  };

  return (
    <>
      <div className="flex max-w-[600px] flex-col gap-8">
        <section className="flex flex-col gap-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Profile</h2>
            <p className="mt-1 text-sm text-gray-500">
              Update your name and view your email address.
            </p>
          </div>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <Input {...form.register("name")} label="Name" name="name" placeholder="John Doe" />
            <Input
              {...form.register("email")}
              label="Email"
              name="email"
              disabled
              placeholder="john.doe@example.com"
            />
            <div className="flex justify-start">
              <Button
                disabled={!form.formState.isValid}
                variant="outline"
                type="submit"
                className="w-fit"
              >
                Save Changes
              </Button>
            </div>
          </form>
        </section>

        <section className="flex flex-col gap-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Security</h2>
            <p className="mt-1 text-sm text-gray-500">{securityDescription}</p>
          </div>
          {isMfaPolicyLoading ? (
            <div className="flex justify-center rounded-lg border border-gray-200 bg-white py-8">
              <LoadingSpinner size={5} />
            </div>
          ) : (
            <>
              {needsBreakGlassPasswordSetup && <BreakGlassPasswordSection />}
              {!canSelfManageMfa ? (
                <div className="flex items-start justify-between gap-4 rounded-lg border border-gray-200 bg-white p-4">
                  <div className="min-w-0">
                    <p className="flex items-center text-sm font-medium text-gray-900">
                      Multi-factor authentication
                      <HelpTooltip content={mfaHelpTooltip} />
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      Managed by your identity provider. Contact your IT administrator to change MFA
                      settings.
                    </p>
                  </div>
                  <Badge
                    variant={BadgeVariant.BLUE}
                    size={BadgeSize.SM}
                    text="Managed by IdP"
                    className="shrink-0"
                  />
                </div>
              ) : (
                <div className="flex items-start justify-between gap-4 rounded-lg border border-gray-200 bg-white p-4">
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center text-sm font-medium text-gray-900">
                      Multi-factor authentication
                      <HelpTooltip content={mfaHelpTooltip} />
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      {mfaEnabled
                        ? "MFA is enabled for your account."
                        : "Add an extra layer of security to your account."}
                    </p>
                    {isOwnerBreakGlass && !mfaEnabled && (
                      <p className="mt-2 text-xs font-medium text-amber-700">
                        Required for organization Owners before turning on &ldquo;Require
                        SSO&rdquo;.
                      </p>
                    )}
                    {isOwnerBreakGlass && mfaEnabled && needsBreakGlassPasswordSetup && (
                      <p className="mt-2 text-xs font-medium text-amber-700">
                        Set an emergency access password above before turning on &ldquo;Require
                        SSO&rdquo;.
                      </p>
                    )}
                    {isBreakGlassReadyState && (
                      <div className="mt-2">
                        <Badge
                          variant={BadgeVariant.GREEN}
                          size={BadgeSize.SM}
                          text="Emergency access ready"
                        />
                      </div>
                    )}
                  </div>
                  <Switch
                    defaultChecked={mfaEnabled}
                    checked={mfaEnabled}
                    onChange={handleMfaToggle}
                    disabled={isDeleting}
                    size="sm"
                  />
                </div>
              )}
            </>
          )}
        </section>
      </div>

      <MfaEnrollmentDialog open={enrollmentModalOpen} onOpenChange={setEnrollmentModalOpen} />
    </>
  );
};

export default AccountSettings;
